<template>
<div class='qiniu-uploader-wrapper' id='qiniu-uploader-wrapper'>
  <el-button id='qiniu-uploader-btn' :type="btnType" :plain="btnPlain" size='large' :disabled='onUploading'>
    {{onUploading ? onUploadingLabel : label}}
  </el-button>
</div>
</template>

<style lang='scss' scoped>
.qiniu-uploader-wrapper {
    position: relative;
}
</style>

<script>
import logger from '@/assets/scripts/logger'
import { lazyload } from '@/assets/scripts/lazy-load-module'
import { generateSessionID } from '@/assets/scripts/misc'

export default {
  data() {
    return {
      uploader: null,
      onUploading: false
    }
  },
  props: {
    label: {
      type: String,
      default: '本地上传'
    },
    onUploadingLabel: {
      type: String,
      default: '上传中...'
    },
    url: String,
    accept: String,
    extensions: Array,
    btnType: {
      type: String,
      default: 'default'
    },
    btnPlain: {
      type: Boolean,
      default: true
    }
  },
  methods: {
    init() {
      let vm = this
      lazyload('plupload').then(plupload => {
        window.plupload = plupload
        window.moxie = plupload.moxie
        lazyload('qiniu').then(_ => {
          vm.uploader = window.Qiniu.uploader({
            runtimes: 'html5,flash,html4', // 上传模式，依次退化
            browse_button: 'qiniu-uploader-btn', // 上传选择的点选按钮，必需
            // 在初始化时，uptoken，uptoken_url，uptoken_func三个参数中必须有一个被设置
            // 切如果提供了多个，其优先级为uptoken > uptoken_url > uptoken_func
            // 其中uptoken是直接提供上传凭证，uptoken_url是提供了获取上传凭证的地址，如果需要定制获取uptoken的过程则可以设置uptoken_func
            // uptoken: '0Lh8Tid6OqWrdSYUXTBOZdyE4AU0KGZb2T1dLdI4:IF5IkkldT5u9QgJQKpe20m3X_1s=:eyJzY29wZSI6InBwdC1zb3VyY2U6dGVzdC5wcHR4IiwiZGVhZGxpbmUiOjE1MTI1NTA4NjF9', // uptoken是上传凭证，由其他程序生成
            uptoken_url: `${process.env.API_ROOT}misc/uptoken`, // Ajax请求uptoken的Url，强烈建议设置（服务端提供）
            // uptoken_func: function(){    // 在需要获取uptoken时，该方法会被调用
            //    // do something
            //    return uptoken;
            // },
            multi_selection: false, // 设置每次只能选择一个文件
            get_new_uptoken: false, // 设置上传文件的时候是否每次都重新获取新的uptoken
            // downtoken_url: '/downtoken',
            // Ajax请求downToken的Url，私有空间时使用，JS-SDK将向该地址POST文件的key和domain，服务端返回的JSON必须包含url字段，url值为该文件的下载地址
            // unique_names: true,              // 默认false，key为文件名。若开启该选项，JS-SDK会为每个文件自动生成key（文件名）
            // save_key: true,                  // 默认false。若在服务端生成uptoken的上传策略中指定了sava_key，则开启，SDK在前端将不对key进行任何处理
            domain: 'p0ha59qxb.bkt.clouddn.com', // bucket域名，下载资源时用到，必需
            container: 'qiniu-uploader-wrapper', // 上传区域DOM ID，默认是browser_button的父元素
            max_retries: 1, // 上传失败最大重试次数
            dragdrop: true, // 开启可拖曳上传
            drop_element: 'qiniu-uploader-wrapper', // 拖曳上传区域元素的ID，拖曳文件或文件夹后可触发上传
            chunk_size: '4mb', // 分块上传时，每块的体积
            auto_start: true, // 选择文件后自动上传，若关闭需要自己绑定事件触发上传
            // 可以使用该参数来限制上传文件的类型，大小等，该参数以对象的形式传入，它包括三个属性：
            filters: {
              max_file_size: '2gb',
              // Specify what files to browse for
              mime_types: [
                { title: 'Custom Files', extensions: vm.extensions.join(',') } // 限定flv后缀上传格式上传
              ]
            },
            init: {
              'BeforeUpload': function(up, file) {
                vm.$emit('update:filename', file.name)
                vm.$emit('update:filesize', file.size || 0)
                vm.$emit('update:uploading', true)
              },
              'UploadProgress': function(up, file) {
                // 每个文件上传时，处理相关的事情
                vm.$emit('update:progress', file.percent)
                logger.debug('progress', file.percent)
              },
              'FileUploaded': function(up, file, info) {
                // 每个文件上传成功后，处理相关的事情
                var domain = up.getOption('domain')
                var res = (typeof info.response === 'string' ? JSON.parse(info.response) : info.response)
                var sourceLink = domain + '/' + res.key // 获取上传成功后的文件的Url
                logger.info('file uploaded', sourceLink)

                // 清理cookie存的Token
                generateSessionID(vm.$store.state.user.info.id, file, { clean: true })

                vm.$emit('complete', { qn_file_id: res.key })
              },
              'Error': function(up, err, errTip) {
                // 上传出错时，处理相关的事情
                if (err.code === -600) {
                  vm.$message({
                    message: '您的文件大小已超出上限',
                    type: 'error'
                  })
                  vm.$emit('update:uploading', false)
                  return
                }
                // 当前session上传的文件与之前上传的不一致，七牛服务端验证不通过
                if (err.status === 614) {
                  vm.$message({
                    message: '文件发生修改，继续请重新选择',
                    type: 'error'
                  })

                  // 清理cookie存的Token
                  generateSessionID(vm.$store.state.user.info.id, err.file, { clean: true })

                  vm.$emit('update:uploading', false)
                  return
                }
                vm.$emit('error', 'qiniu')
                logger.error(`qiniu upload fail: ${errTip}`)
                vm.$message({
                  message: errTip,
                  type: 'error'
                })
              },
              'Key': function(up, file) {
                // 若想在前端对每个文件的key进行个性化处理，可以配置该函数
                // 该配置必须要在unique_names: false，save_key: false时才生效
                return generateSessionID(vm.$store.state.user.info.id, file)
              }
            }
          })
        })
      })
    }
  },
  mounted() {
    this.init()
  },
  destroyed() {
    this.uploader.stop()
  }
}
</script>



// WEBPACK FOOTER //
// qiniu_uploader.input.vue?340d22b9